import CloseIcon from '@mui/icons-material/Close'
import { Box, Button, IconButton, List, ListItem, ListItemButton, ListItemText, Typography } from '@mui/material'
import clsx from 'clsx'
import { useFmsNotificationsContext } from 'context/FmsNotificationsContext'
import { useUser } from 'hooks/user'
import type { FmsNotificationInboxItem } from 'hooks/useFmsNotifications'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { patchNotificationPrefs } from 'services/notificationPrefs'
import {
  isRobotNotificationGroup,
  isStationNotificationGroup,
  notificationSubtitle,
  resolveRobotGroupLabels,
  resolveStationGroupLabels,
  robotGroupAccentColor,
} from 'utils/notificationDisplay'
import { PATH } from 'utils/constants'
import { snoozeUntilAtMinute } from 'utils/notificationSnoozeSync'

import style from './FmsNotifications.module.css'

function groupLabel(
  key: string,
  items: {
    robot_id?: number | null
    station_pk?: number | null
    station_id?: string | null
    station_name?: string | null
    title: string
    payload?: { station_pk?: unknown; station_id?: string; station_name?: string; [key: string]: unknown }
  }[]
) {
  if (key.startsWith('odyssey:')) {
    return `Task #${key.split(':')[1]}`
  }
  if (isStationNotificationGroup(key) && items[0]) {
    return resolveStationGroupLabels(items[0]).name
  }
  if (isRobotNotificationGroup(key) && items[0]) {
    return resolveRobotGroupLabels(items[0]).name
  }
  return 'Other'
}

function AssetGroupHeading({
  name,
  serial,
  onOpenRobot,
}: {
  name: string
  serial: string
  onOpenRobot?: () => void
}) {
  const label = (
    <>
      {name}
      {serial ? (
        <Typography component="span" className={style.groupTitleSerialInline}>
          {' '}
          · {serial}
        </Typography>
      ) : null}
    </>
  )

  return (
    <Box className={style.groupTitle}>
      <Typography variant="caption" component="div" className={style.groupTitleName}>
        {onOpenRobot ? (
          <button
            type="button"
            className={style.groupTitleLink}
            onClick={e => {
              e.stopPropagation()
              onOpenRobot()
            }}
          >
            {label}
          </button>
        ) : (
          label
        )}
      </Typography>
    </Box>
  )
}

function severityClass(severity: string): string {
  if (severity === 'error') return style.severityError
  if (severity === 'warning') return style.severityWarn
  if (severity === 'ok') return style.severityOk
  return style.severityInfo
}

function NotificationRow({
  item,
  highlighted,
  onOpen,
  onDismiss,
}: {
  item: FmsNotificationInboxItem
  highlighted: boolean
  onOpen: () => void
  onDismiss: () => void
}) {
  const robotSerial = resolveRobotGroupLabels(item).serial
  const stationSerial = resolveStationGroupLabels(item).serial
  const subtitle = notificationSubtitle(item, robotSerial || stationSerial)

  return (
    <ListItem
      disablePadding
      className={highlighted ? style.rowHighlighted : undefined}
      secondaryAction={
        <IconButton
          edge="end"
          size="small"
          aria-label="dismiss"
          onClick={e => {
            e.stopPropagation()
            onDismiss()
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      }
    >
      <ListItemButton onClick={onOpen} className={style.rowButton}>
        <span className={`${style.severityStripe} ${severityClass(item.severity)}`} aria-hidden />
        <ListItemText
          primary={item.title}
          secondary={subtitle || undefined}
          primaryTypographyProps={{ noWrap: false, className: style.rowPrimary }}
          secondaryTypographyProps={{ noWrap: false, className: style.rowSecondary }}
        />
      </ListItemButton>
    </ListItem>
  )
}

type Props = {
  onRequestClose?: () => void
  onOpenHistory?: () => void
  featuredId?: number | null
  /** Compact shell when inbox has no unread items (short empty / all-caught-up). */
  compact?: boolean
}

/** Unread inbox body for the header notifications popover. */
export function NotificationsInboxPanel({
  onRequestClose,
  onOpenHistory,
  featuredId = null,
  compact = false,
}: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const user = useUser()
  const favoriteRobotIds = useMemo(() => new Set(user?.state.favoriteRobotIds ?? []), [user?.state.favoriteRobotIds])
  const favoriteStationIds = useMemo(
    () => new Set(user?.state.favoriteStationIds ?? []),
    [user?.state.favoriteStationIds]
  )
  const {
    items,
    groupedItems,
    unreadCount,
    dismiss,
    markAllRead,
    openNotification,
    hideAllToastHighlights,
    webPushUiState,
    enableWebPush,
    isSnoozed,
    applyMutedUntil,
  } = useFmsNotificationsContext()

  const unreadGrouped = useMemo(() => {
    const rows = groupedItems
      .map(([key, groupItems]) => [key, groupItems.filter(i => !i.read)] as const)
      .filter(([, groupItems]) => groupItems.length > 0)
    const favorite = (key: string) => {
      if (key.startsWith('station:')) return favoriteStationIds.has(Number(key.slice('station:'.length)))
      if (key.startsWith('robot:')) return favoriteRobotIds.has(Number(key.slice('robot:'.length)))
      return false
    }
    return [...rows.filter(([key]) => favorite(key)), ...rows.filter(([key]) => !favorite(key))]
  }, [groupedItems, favoriteRobotIds, favoriteStationIds])

  const handleOpenNotification = (item: FmsNotificationInboxItem) => {
    openNotification(item)
    onRequestClose?.()
  }

  const handleOpenRobot = (robotId: number) => {
    navigate(`${PATH.ROBOT}${robotId}`)
    onRequestClose?.()
  }

  const handleSnoozeOneHour = () => {
    const until = snoozeUntilAtMinute(new Date())
    until.setHours(until.getHours() + 1)
    applyMutedUntil(until.toISOString())
    void patchNotificationPrefs({ muted_until: until.toISOString() }).catch(err => {
      console.warn('[Notifications] failed to persist snooze', err)
    })
    hideAllToastHighlights()
    onRequestClose?.()
  }

  const emptyMessage = items.length === 0 ? t('header.no_notifications') : t('header.all_caught_up')

  return (
    <Box className={clsx(style.sandwich, compact && style.sandwichCompact)} onClick={e => e.stopPropagation()}>
      <Box className={style.menuHeader}>
        <Typography variant="subtitle2" className={style.menuHeaderTitle}>
          {t('header.notifications')}
        </Typography>
        <Box className={style.menuHeaderActions}>
          <button
            type="button"
            className={style.headerTextAction}
            onClick={() => {
              hideAllToastHighlights()
              onOpenHistory?.()
            }}
          >
            {t('header.view_all')}
          </button>
          {unreadCount > 0 ? (
            <button type="button" className={style.headerTextAction} onClick={() => markAllRead()}>
              {t('header.mark_all_read')}
            </button>
          ) : null}
          {!isSnoozed ? (
            <button
              type="button"
              className={style.headerTextAction}
              onClick={handleSnoozeOneHour}
              aria-label={t('header.snooze_one_hour')}
            >
              {t('header.snooze_one_hour')}
            </button>
          ) : null}
        </Box>
      </Box>

      {webPushUiState === 'prompt' ? (
        <Box className={style.pushPrompt}>
          <Button
            fullWidth
            size="small"
            variant="outlined"
            onClick={() => {
              void enableWebPush()
            }}
          >
            {t('header.enable_push')}
          </Button>
        </Box>
      ) : null}
      {webPushUiState === 'denied' ? (
        <Typography className={style.menuEmptyCompact} variant="caption" component="div">
          {t('header.push_blocked')}
        </Typography>
      ) : null}

      {compact ? (
        <Typography className={style.menuEmptyCompact} variant="body2">
          {emptyMessage}
        </Typography>
      ) : (
        <List dense disablePadding className={style.menuList}>
          {unreadGrouped.map(([key, groupItems]) => {
            const first = groupItems[0]
            const showRobotGroup = isRobotNotificationGroup(key) && Boolean(first)
            const showStationGroup = isStationNotificationGroup(key) && Boolean(first)
            const showAssetGroup = showRobotGroup || showStationGroup
            const accentColor = showRobotGroup && first ? robotGroupAccentColor(first) : undefined
            const groupClassName = showAssetGroup ? style.robotGroup : style.genericGroup
            const assetLabels = first
              ? showStationGroup
                ? resolveStationGroupLabels(first)
                : resolveRobotGroupLabels(first)
              : null
            const robotId = showRobotGroup && first?.robot_id != null ? first.robot_id : null

            return (
              <Box
                key={key}
                className={groupClassName}
                style={accentColor ? ({ '--robot-group-accent': accentColor } as React.CSSProperties) : undefined}
              >
                {showAssetGroup && assetLabels ? (
                  <AssetGroupHeading
                    name={assetLabels.name}
                    serial={assetLabels.serial}
                    onOpenRobot={robotId != null ? () => handleOpenRobot(robotId) : undefined}
                  />
                ) : (
                  <Typography className={style.groupTitle} variant="caption">
                    {groupLabel(key, groupItems)}
                  </Typography>
                )}
                {key.startsWith('odyssey:') && first ? (
                  <Typography className={style.groupSubTitle} variant="caption">
                    {groupLabel(key, groupItems)}
                  </Typography>
                ) : null}
                {groupItems.map(item => (
                  <NotificationRow
                    key={item.id}
                    item={item}
                    highlighted={item.id === featuredId}
                    onOpen={() => handleOpenNotification(item)}
                    onDismiss={() => dismiss(item.id)}
                  />
                ))}
              </Box>
            )
          })}
        </List>
      )}
    </Box>
  )
}

/** Featured unread toast id — used to auto-open the inbox popover. */
export function useFeaturedNotificationId(): number | null {
  const { items } = useFmsNotificationsContext()
  return useMemo(() => {
    const featured = items.find(i => i.toastVisible && !i.read)
    return featured?.id ?? null
  }, [items])
}
